import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { 
  getInvestmentAreas, 
  getInvestmentProducts, 
  getCurrencies, 
  getPlatforms,
  createInvestmentArea,
  updateInvestmentArea,
  deleteInvestmentArea,
  createInvestmentProduct,
  updateInvestmentProduct,
  deleteInvestmentProduct,
  getAutomaticInvestments,
  createAutomaticInvestment,
  updateAutomaticInvestment,
  deleteAutomaticInvestment,
  processAutomaticInvestments,
  InvestmentArea,
  InvestmentProduct,
  Currency,
  Platform,
  AutomaticInvestment
} from '../services/investmentService';

const Configurations: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'areas' | 'products' | 'automatic'>('areas');
  const [editingArea, setEditingArea] = useState<InvestmentArea | null>(null);
  const [editingProduct, setEditingProduct] = useState<InvestmentProduct | null>(null);
  const [editingAutomatic, setEditingAutomatic] = useState<AutomaticInvestment | null>(null);
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showAutomaticForm, setShowAutomaticForm] = useState(false);

  const queryClient = useQueryClient();

  // Queries
  const { data: areas, isLoading: areasLoading } = useQuery('investment-areas', getInvestmentAreas);
  const { data: products, isLoading: productsLoading } = useQuery('investment-products', getInvestmentProducts);
  const { data: currencies } = useQuery('currencies', getCurrencies);
  const { data: platforms } = useQuery('platforms', getPlatforms);
  const { data: automaticInvestments, isLoading: automaticLoading } = useQuery('automatic-investments', getAutomaticInvestments);

  // Mutations
  const createAreaMutation = useMutation(createInvestmentArea, {
    onSuccess: () => {
      queryClient.invalidateQueries('investment-areas');
      setShowAreaForm(false);
    },
  });

  const updateAreaMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<InvestmentArea> }) => updateInvestmentArea(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('investment-areas');
        setEditingArea(null);
      },
    }
  );

  const deleteAreaMutation = useMutation(deleteInvestmentArea, {
    onSuccess: () => {
      queryClient.invalidateQueries('investment-areas');
    },
  });

  const createProductMutation = useMutation(createInvestmentProduct, {
    onSuccess: () => {
      queryClient.invalidateQueries('investment-products');
      setShowProductForm(false);
    },
  });

  const updateProductMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<InvestmentProduct> }) => updateInvestmentProduct(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('investment-products');
        setEditingProduct(null);
      },
    }
  );

  const deleteProductMutation = useMutation(deleteInvestmentProduct, {
    onSuccess: () => {
      queryClient.invalidateQueries('investment-products');
    },
  });

  const createAutomaticMutation = useMutation(createAutomaticInvestment, {
    onSuccess: () => {
      queryClient.invalidateQueries('automatic-investments');
      setShowAutomaticForm(false);
    },
  });

  const updateAutomaticMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<AutomaticInvestment> }) => updateAutomaticInvestment(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('automatic-investments');
        setEditingAutomatic(null);
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar investimento automático:', error);
        alert('Erro ao atualizar investimento automático: ' + (error.response?.data?.message || error.message));
      },
    }
  );

  const deleteAutomaticMutation = useMutation(deleteAutomaticInvestment, {
    onSuccess: () => {
      queryClient.invalidateQueries('automatic-investments');
    },
  });

  const processAutomaticMutation = useMutation(processAutomaticInvestments, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('capital-increases');
      queryClient.invalidateQueries('dashboard-overview');
      alert(`Processados ${data.data.processed} aumentos de capital automáticos de ${data.data.total} datas configuradas.`);
    },
    onError: (error: any) => {
      alert('Erro ao processar investimentos automáticos: ' + (error.response?.data?.message || error.message));
    },
  });

  const handleAreaSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      platform_id: parseInt(formData.get('platform_id') as string),
      target_percentage: parseFloat(formData.get('target_percentage') as string),
    };

    if (editingArea) {
      updateAreaMutation.mutate({ id: editingArea.id, data });
    } else {
      createAreaMutation.mutate(data);
    }
  };

  const handleProductSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      area_id: parseInt(formData.get('area_id') as string),
      name: formData.get('name') as string,
      currency_id: parseInt(formData.get('currency_id') as string),
      target_percentage: parseFloat(formData.get('target_percentage') as string),
    };

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, data });
    } else {
      createProductMutation.mutate(data);
    }
  };

  const handleAutomaticSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const frequency = formData.get('frequency') as 'weekly' | 'monthly';
    const data = {
      product_id: parseInt(formData.get('product_id') as string),
      amount: parseFloat(formData.get('amount') as string),
      start_date: formData.get('start_date') as string,
      frequency: frequency,
      day_of_week: frequency === 'weekly' ? parseInt(formData.get('day_of_week') as string) : undefined,
      day_of_month: frequency === 'monthly' ? (formData.get('day_of_month') as string ? parseInt(formData.get('day_of_month') as string) : null) : undefined,
      is_active: formData.get('is_active') === 'on',
      notes: (formData.get('notes') as string) || undefined,
    };

    if (editingAutomatic) {
      console.log('Updating investment with ID:', editingAutomatic.id, 'Data:', data);
      updateAutomaticMutation.mutate({ id: editingAutomatic.id, data });
    } else {
      createAutomaticMutation.mutate(data);
    }
  };

  const formatPercentage = (value: number | string | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
    return `${numValue.toFixed(2)}%`;
  };

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const frequency = e.target.value;
    const dayOfMonthField = document.getElementById('day-of-month-field');
    const dayOfWeekField = document.getElementById('day-of-week-field');
    
    if (frequency === 'weekly') {
      dayOfMonthField!.style.display = 'none';
      dayOfWeekField!.style.display = 'block';
    } else {
      dayOfMonthField!.style.display = 'block';
      dayOfWeekField!.style.display = 'none';
    }
  };

  // Configurar visibilidade inicial dos campos quando o modal é aberto
  useEffect(() => {
    if (showAutomaticForm || editingAutomatic) {
      const frequency = editingAutomatic?.frequency || 'monthly';
      const dayOfMonthField = document.getElementById('day-of-month-field');
      const dayOfWeekField = document.getElementById('day-of-week-field');
      
      if (frequency === 'weekly') {
        dayOfMonthField!.style.display = 'none';
        dayOfWeekField!.style.display = 'block';
      } else {
        dayOfMonthField!.style.display = 'block';
        dayOfWeekField!.style.display = 'none';
      }
    }
  }, [showAutomaticForm, editingAutomatic]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
        <p className="text-gray-600">Configure as áreas de investimento e produtos</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('areas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'areas'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Áreas de Investimento
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'products'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Produtos de Investimento
          </button>
          <button
            onClick={() => setActiveTab('automatic')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'automatic'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Investimentos Automáticos
          </button>
        </nav>
      </div>

      {/* Areas Tab */}
      {activeTab === 'areas' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Áreas de Investimento</h2>
            <button
              onClick={() => setShowAreaForm(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Área
            </button>
          </div>

          {/* Areas Table */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Plataforma</th>
                    <th>% Alvo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {areasLoading ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : (
                    areas?.map((area) => (
                      <tr key={area.id}>
                        <td className="font-medium">{area.name}</td>
                        <td>{area.platform_name}</td>
                        <td>{formatPercentage(area.target_percentage)}</td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingArea(area)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteAreaMutation.mutate(area.id)}
                              className="text-danger-600 hover:text-danger-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Area Form Modal */}
          {(showAreaForm || editingArea) && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingArea ? 'Editar Área' : 'Nova Área'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAreaForm(false);
                      setEditingArea(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleAreaSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingArea?.name || ''}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plataforma
                    </label>
                    <select name="platform_id" className="input" required>
                      <option value="">Selecionar plataforma</option>
                      {platforms?.map((platform) => (
                        <option
                          key={platform.id}
                          value={platform.id}
                          selected={editingArea?.platform_id === platform.id}
                        >
                          {platform.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Percentagem Alvo (%)
                    </label>
                    <input
                      type="number"
                      name="target_percentage"
                      step="0.01"
                      min="0"
                      max="100"
                      defaultValue={editingArea?.target_percentage || ''}
                      className="input"
                      required
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAreaForm(false);
                        setEditingArea(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={createAreaMutation.isLoading || updateAreaMutation.isLoading}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {editingArea ? 'Atualizar' : 'Criar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Produtos de Investimento</h2>
            <button
              onClick={() => setShowProductForm(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Produto
            </button>
          </div>

          {/* Products Table */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Área</th>
                    <th>Moeda</th>
                    <th>% Alvo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {productsLoading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : (
                    products?.map((product) => (
                      <tr key={product.id}>
                        <td className="font-medium">{product.name}</td>
                        <td>{product.area_name}</td>
                        <td>{product.currency_code}</td>
                        <td>{formatPercentage(product.target_percentage)}</td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingProduct(product)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteProductMutation.mutate(product.id)}
                              className="text-danger-600 hover:text-danger-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Product Form Modal */}
          {(showProductForm || editingProduct) && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleProductSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingProduct?.name || ''}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Área
                    </label>
                    <select name="area_id" className="input" required>
                      <option value="">Selecionar área</option>
                      {areas?.map((area) => (
                        <option
                          key={area.id}
                          value={area.id}
                          selected={editingProduct?.area_id === area.id}
                        >
                          {area.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Moeda
                    </label>
                    <select name="currency_id" className="input" required>
                      <option value="">Selecionar moeda</option>
                      {currencies?.map((currency) => (
                        <option
                          key={currency.id}
                          value={currency.id}
                          selected={editingProduct?.currency_id === currency.id}
                        >
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Percentagem Alvo (%)
                    </label>
                    <input
                      type="number"
                      name="target_percentage"
                      step="0.01"
                      min="0"
                      max="100"
                      defaultValue={editingProduct?.target_percentage || ''}
                      className="input"
                      required
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowProductForm(false);
                        setEditingProduct(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={createProductMutation.isLoading || updateProductMutation.isLoading}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {editingProduct ? 'Atualizar' : 'Criar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Automatic Investments Tab */}
      {activeTab === 'automatic' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Investimentos Automáticos</h2>
            <div className="flex space-x-3">
              <button
                onClick={() => processAutomaticMutation.mutate()}
                disabled={processAutomaticMutation.isLoading}
                className="btn btn-secondary"
              >
                {processAutomaticMutation.isLoading ? 'Processando...' : 'Processar Agora'}
              </button>
              <button
                onClick={() => setShowAutomaticForm(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Investimento Automático
              </button>
            </div>
          </div>

          {/* Automatic Investments Table */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                 <thead>
                   <tr>
                     <th>Produto</th>
                     <th>Área</th>
                     <th>Valor</th>
                     <th>Frequência</th>
                     <th>Data de Início</th>
                     <th>Estado</th>
                     <th>Ações</th>
                   </tr>
                 </thead>
                <tbody>
                  {automaticLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : (
                    automaticInvestments?.map((investment) => {
                      const getFrequencyText = () => {
                        if (investment.frequency === 'weekly') {
                          const days = ['', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
                          return `Semanal (${days[investment.day_of_week || 1]})`;
                        } else {
                          if (investment.day_of_month === null) {
                            return 'Mensal (Final do mês)';
                          } else {
                            return `Mensal (Dia ${investment.day_of_month})`;
                          }
                        }
                      };

                      return (
                        <tr key={investment.id}>
                          <td className="font-medium">{investment.product_name}</td>
                          <td>{investment.area_name}</td>
                          <td>
                            {investment.amount.toLocaleString('pt-PT', {
                              style: 'currency',
                              currency: investment.currency_code
                            })}
                          </td>
                          <td>{getFrequencyText()}</td>
                          <td>{new Date(investment.start_date).toLocaleDateString('pt-PT')}</td>
                          <td>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              investment.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {investment.is_active ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingAutomatic(investment)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteAutomaticMutation.mutate(investment.id)}
                              className="text-danger-600 hover:text-danger-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Automatic Investment Form Modal */}
          {(showAutomaticForm || editingAutomatic) && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingAutomatic ? 'Editar Investimento Automático' : 'Novo Investimento Automático'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAutomaticForm(false);
                      setEditingAutomatic(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleAutomaticSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Produto
                    </label>
                    <select name="product_id" className="input" required>
                      <option value="">Selecionar produto</option>
                      {products?.map((product) => (
                        <option
                          key={product.id}
                          value={product.id}
                          selected={editingAutomatic?.product_id === product.id}
                        >
                          {product.name} ({product.area_name})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    <input
                      type="number"
                      name="amount"
                      step="0.01"
                      min="0"
                      defaultValue={editingAutomatic?.amount || ''}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      defaultValue={editingAutomatic?.start_date ? editingAutomatic.start_date.split('T')[0] : ''}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequência
                    </label>
                    <select name="frequency" className="input" required onChange={handleFrequencyChange}>
                      <option value="monthly" selected={editingAutomatic?.frequency === 'monthly' || !editingAutomatic}>
                        Mensal
                      </option>
                      <option value="weekly" selected={editingAutomatic?.frequency === 'weekly'}>
                        Semanal
                      </option>
                    </select>
                  </div>

                  <div id="day-of-month-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dia do Mês
                    </label>
                    <select name="day_of_month" className="input">
                      <option value="">Final do mês</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option
                          key={day}
                          value={day}
                          selected={editingAutomatic?.day_of_month === day}
                        >
                          Dia {day}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div id="day-of-week-field" style={{ display: 'none' }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dia da Semana
                    </label>
                    <select name="day_of_week" className="input">
                      <option value="1" selected={editingAutomatic?.day_of_week === 1}>
                        Segunda-feira
                      </option>
                      <option value="2" selected={editingAutomatic?.day_of_week === 2}>
                        Terça-feira
                      </option>
                      <option value="3" selected={editingAutomatic?.day_of_week === 3}>
                        Quarta-feira
                      </option>
                      <option value="4" selected={editingAutomatic?.day_of_week === 4}>
                        Quinta-feira
                      </option>
                      <option value="5" selected={editingAutomatic?.day_of_week === 5}>
                        Sexta-feira
                      </option>
                      <option value="6" selected={editingAutomatic?.day_of_week === 6}>
                        Sábado
                      </option>
                      <option value="7" selected={editingAutomatic?.day_of_week === 7}>
                        Domingo
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notas
                    </label>
                    <textarea
                      name="notes"
                      defaultValue={editingAutomatic?.notes || ''}
                      className="input"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      defaultChecked={editingAutomatic?.is_active ?? true}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Ativo
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAutomaticForm(false);
                        setEditingAutomatic(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={createAutomaticMutation.isLoading || updateAutomaticMutation.isLoading}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {editingAutomatic ? 'Atualizar' : 'Criar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Configurations;
